import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PublicIcon from '@mui/icons-material/Public';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

import type {
  CustomerChargebackAlert,
  CustomerDispute,
  CustomerSubscription,
  DashboardCustomer,
  DashboardCustomerPaymentMethod,
} from '@models/customers';
import type { PaymentCurrency } from '@models/payments';
import {
  formatAmountWithCurrency,
  formatMaskedCardDigits,
  formatMinorAmount,
  formatShortDate,
  getCustomerAvatarColorIndex,
  getCustomerCountry,
  getCustomerDisplayName,
  getCustomerInitials,
  getCountryName,
  buildCurrencyLookup,
  formatDebtAmount,
  getPaymentMethodLabel,
  getPaymentMethodSubLabel,
  getSubscriptionLabel,
  humanizeLabel,
  isMigratedCustomer,
} from '@features/customers/helpers';
import {
  formatPaymentDate,
  getCustomerProfileUrl,
  getPrimerDashboardPaymentUrl,
} from '@features/payments/helpers';

import ExternalLinkButton from '@components/ui/ExternalLinkButton';
import StatusChip, { type StatusChipTone } from '@components/ui/StatusChip';
import EditPaymentMethodButton from '../EditPaymentMethodButton';
import SubscriptionBadge from '../SubscriptionBadge';
import SubscriptionDetailDrawer from '../SubscriptionDetailDrawer';
import ExportRefundButton from '../ExportRefundButton';
import CustomerOverviewTab from './CustomerOverviewTab';
import CustomerPaymentsTab from './CustomerPaymentsTab';
import CustomerScheduledPaymentsTab from './CustomerScheduledPaymentsTab';
import CustomerIncidentsTab from './CustomerIncidentsTab';
import PaymentMethodBrandBadge from './PaymentMethodBrandBadge';
import s from './CustomerDetailPanel.module.css';
import CancelDebtsButton from '../CancelDebtsButton';

const COPIED_RESET_MS = 1500;
const AVATAR_CLASSES = [s.avatar0, s.avatar1, s.avatar2, s.avatar3, s.avatar4, s.avatar5];

const DRAWER_TABS = [
  'overview',
  'payments',
  'scheduledPayments',
  'subscriptions',
  'methods',
  'debt',
  'disputes',
  'chargebacks',
  'refunds',
  'incidents',
] as const;

type DrawerTab = (typeof DRAWER_TABS)[number];

function parseTabParam(value: string | null): DrawerTab | null {
  return value && (DRAWER_TABS as readonly string[]).includes(value) ? (value as DrawerTab) : null;
}

type CopyKey = 'id' | 'email' | 'externalId' | `sub:${string}`;

type CustomerDetailPanelProps = {
  customer: DashboardCustomer | null;
};

function formatDateTimeLabel(value: string | null): string {
  if (!value) return 'N/A';
  const fd = formatPaymentDate(new Date(value));
  return `${fd.date} · ${fd.time}`;
}

function getDebtStatusTone(status: string): StatusChipTone {
  const value = status.toLowerCase();
  if (value === 'failed') return 'danger';
  if (value === 'processing' || value === 'pending') return 'warning';
  if (['collected', 'success', 'succeeded', 'completed'].includes(value)) return 'success';
  return 'neutral';
}

type FinancialEventItem = {
  key: string;
  title: string;
  detail: string;
  /** Optional highlighted line under `detail` (e.g. an active recurring-payment block). */
  subDetail?: string;
  statusLabel: string;
  statusTone?: StatusChipTone;
  icon?: ReactNode;
  action?: ReactNode;
};

const DISPUTE_TYPE_LABELS: Record<string, string> = {
  DISPUTE: 'Dispute',
  RETRIEVAL: 'Retrieval',
  FRAUD_WARNING: 'Fraud warning',
};

function getDisputeStatusTone(status: string): StatusChipTone {
  const value = status.toUpperCase();
  if (value === 'WON') return 'success';
  if (value === 'CHALLENGED' || value === 'UNDER_REVIEW') return 'warning';
  if (value === 'OPEN' || value === 'OPENED' || value === 'LOST') return 'danger';
  return 'neutral';
}

function pickLatestDisputeEvent(a: CustomerDispute, b: CustomerDispute): CustomerDispute {
  const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  if (Math.abs(diff) < 60_000 && a.eventType !== b.eventType) {
    return a.eventType === 'DISPUTE.STATUS' ? a : b;
  }
  return diff >= 0 ? a : b;
}

function latestDisputePerCase(disputes: CustomerDispute[]): CustomerDispute[] {
  const byCase = new Map<string, CustomerDispute>();
  for (const dispute of disputes) {
    const key = dispute.request?.processorDisputeId || dispute.id;
    const current = byCase.get(key);
    byCase.set(key, current ? pickLatestDisputeEvent(dispute, current) : dispute);
  }
  return [...byCase.values()];
}

function buildDisputeItems(disputes: CustomerDispute[]): FinancialEventItem[] {
  return latestDisputePerCase(disputes).map((dispute) => {
    const type = dispute.request?.type;
    const processor = dispute.request?.processor ?? dispute.request?.processorId ?? '';
    return {
      key: dispute.id,
      title: formatMinorAmount(dispute.amount, dispute.currency),
      detail: [
        type && type !== 'DISPUTE'
          ? (DISPUTE_TYPE_LABELS[type] ?? humanizeLabel(type.toLowerCase()))
          : '',
        dispute.request?.reason ?? '',
        processor ? humanizeLabel(processor.toLowerCase()) : '',
        formatShortDate(dispute.createdAt),
      ]
        .filter(Boolean)
        .join(' · '),
      statusLabel: humanizeLabel(dispute.status.toLowerCase()),
      statusTone: getDisputeStatusTone(dispute.status),
      action: dispute.providerId ? (
        <a
          href={getPrimerDashboardPaymentUrl(dispute.providerId)}
          target="_blank"
          rel="noopener noreferrer"
          className={s.eventLink}
          title="View payment in Primer"
          aria-label="View payment in Primer (opens in new tab)">
          <OpenInNewIcon sx={{ fontSize: 15 }} />
        </a>
      ) : undefined,
    };
  });
}

function buildRefundItems(
  customer: DashboardCustomer,
  currencyById: Map<string, PaymentCurrency>,
): FinancialEventItem[] {
  return (customer.refunds ?? []).map((refund) => ({
    key: refund.id,
    // refund.amount is minor units in the refund's own currency — there is no euro-normalized field.
    title: formatAmountWithCurrency(refund.amount, refund.currencyId, currencyById),
    detail: [
      (refund.reasons ?? []).map(humanizeLabel).join(', '),
      refund.paymentProviderId ?? '',
      formatShortDate(refund.createdAt),
    ]
      .filter(Boolean)
      .join(' · '),
    statusLabel: refund.status === 'completed' ? 'Refunded' : humanizeLabel(refund.status),
    action: <ExportRefundButton customer={customer} refund={refund} />,
  }));
}

function getRecurringBlockLine(method: DashboardCustomerPaymentMethod): string | undefined {
  if (!method.preventRecurringPaymentsTill) return undefined;
  const till = new Date(method.preventRecurringPaymentsTill);
  if (till.getTime() <= Date.now()) return undefined;
  return `Recurring blocked till ${formatDateTimeLabel(method.preventRecurringPaymentsTill)}`;
}

function buildMethodItems(methods: DashboardCustomerPaymentMethod[]): FinancialEventItem[] {
  return methods.map((method) => ({
    key: method.id,
    icon: <PaymentMethodBrandBadge method={method} />,
    title: getPaymentMethodLabel(method),
    detail: getPaymentMethodSubLabel(method),
    subDetail: getRecurringBlockLine(method),
    statusLabel: humanizeLabel(method.status),
    action: <EditPaymentMethodButton method={method} />,
  }));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getChargebackAlertTypeLabel(alert: CustomerChargebackAlert): string {
  if (alert.provider === 'stripe') return 'Stripe';
  if (isRecord(alert.externalData) && typeof alert.externalData.type === 'string') {
    return alert.externalData.type;
  }
  return humanizeLabel(alert.provider.toLowerCase());
}

function getChargebackStatusTone(status: string): StatusChipTone {
  const value = status.toLowerCase();
  if (value === 'new' || value === 'open') return 'danger';
  if (value.includes('completed') || value.includes('refunded')) return 'success';
  if (value === 'processing' || value === 'pending') return 'warning';
  return 'neutral';
}

function buildChargebackItems(alerts: CustomerChargebackAlert[]): FinancialEventItem[] {
  return alerts.map((alert) => ({
    key: alert.id,
    title: formatMinorAmount(alert.paymentAmount, alert.paymentCurrency),
    detail: [
      getChargebackAlertTypeLabel(alert),
      alert.paymentDescriptor ?? '',
      formatMaskedCardDigits(alert.cardLastDigits),
      formatShortDate(alert.receivedAt ?? alert.createdAt),
    ]
      .filter(Boolean)
      .join(' · '),
    statusLabel: humanizeLabel(alert.status.toLowerCase()),
    statusTone: getChargebackStatusTone(alert.status),
  }));
}

function FinancialEventCard({
  title,
  items,
  emptyText,
}: {
  title: string;
  items: FinancialEventItem[];
  emptyText: string;
}) {
  return (
    <div className={s.listCard}>
      <header className={s.cardHeader}>
        <h3 className={s.cardTitle}>{title}</h3>
        {items.length > 0 && <span className={s.countBadge}>{items.length}</span>}
      </header>
      {items.length === 0 ? (
        <div className={s.tabEmpty}>{emptyText}</div>
      ) : (
        <ul className={s.eventList}>
          {items.map((item) => (
            <li key={item.key} className={s.eventItem}>
              {item.icon}
              <div className={s.cardListBody}>
                <div className={s.eventTitle}>{item.title}</div>
                <div className={s.eventDetail}>{item.detail || '—'}</div>
                {item.subDetail && <div className={s.eventSubDetail}>{item.subDetail}</div>}
              </div>
              {item.statusTone ? (
                <StatusChip label={item.statusLabel} tone={item.statusTone} />
              ) : (
                <span className={s.eventStatusPill}>{item.statusLabel}</span>
              )}
              {item.action}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CustomerDetailPanel({ customer }: CustomerDetailPanelProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTabParam(searchParams.get('tab')) ?? 'overview';
  const [copiedKey, setCopiedKey] = useState<CopyKey | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<CustomerSubscription | null>(
    null,
  );

  const selectTab = (next: DrawerTab) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'overview') {
          params.delete('tab');
        } else {
          params.set('tab', next);
        }
        return params;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    setCopiedKey(null);
    setSelectedSubscription(null);
  }, [customer?.id]);

  if (!customer) {
    return (
      <main className={s.panel}>
        <div className={s.panelEmpty}>Select a customer to see their details.</div>
      </main>
    );
  }

  const name = getCustomerDisplayName(customer);
  const initials = getCustomerInitials(customer);
  const avatarClass = AVATAR_CLASSES[getCustomerAvatarColorIndex(customer)];
  const countryName = getCountryName(getCustomerCountry(customer));
  const profileUrl = getCustomerProfileUrl({
    externalId: customer.externalId || '',
    email: customer.email,
    brandCode: customer.brand?.brandCode,
  });

  const copyText = (text: string, key: CopyKey) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey((curr) => (curr === key ? null : curr)), COPIED_RESET_MS);
      })
      .catch(() => {});
  };

  const disputeItems = buildDisputeItems(customer.disputes ?? []);
  const chargebackItems = buildChargebackItems(customer.chargebackAlerts ?? []);
  const currencyById = buildCurrencyLookup(customer);

  const renderCopyButton = (text: string, key: CopyKey, label: string) => (
    <button
      type="button"
      className={s.copyBtn}
      // Copying inside a clickable row must not also open that row's drawer.
      onClick={(e) => {
        e.stopPropagation();
        copyText(text, key);
      }}
      aria-label={label}>
      {copiedKey === key ? (
        <CheckIcon sx={{ fontSize: 13 }} />
      ) : (
        <ContentCopyIcon sx={{ fontSize: 13 }} />
      )}
    </button>
  );

  const tabs: { key: DrawerTab; label: string; count?: number }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'payments', label: 'Payments', count: customer.payments?.length ?? 0 },
    {
      key: 'scheduledPayments',
      label: 'Scheduled payments',
      count: customer.scheduledPayments?.length ?? 0,
    },
    { key: 'subscriptions', label: 'Subscriptions', count: customer.subscriptions?.length ?? 0 },
    {
      key: 'methods',
      label: 'Payment methods',
      count: customer.customerPaymentMethods?.length ?? 0,
    },
    { key: 'debt', label: 'Debt', count: customer.debtCollections?.length ?? 0 },
    { key: 'disputes', label: 'Disputes', count: disputeItems.length },
    { key: 'chargebacks', label: 'Chargebacks', count: chargebackItems.length },
    { key: 'refunds', label: 'Refunds', count: customer.refunds?.length ?? 0 },
    { key: 'incidents', label: 'Incidents', count: customer.incidents?.length ?? 0 },
  ];

  return (
    <main className={s.panel}>
      <header className={s.header}>
        <span className={`${s.avatar} ${avatarClass}`}>{initials}</span>
        <div className={s.headerBody}>
          <div className={s.nameRow}>
            <h2 className={s.name}>{name || 'N/A'}</h2>
            {customer.brand?.brandCode && (
              <span className={s.appChip}>{customer.brand.brandCode.toUpperCase()}</span>
            )}
            {isMigratedCustomer(customer) && (
              <span className={s.migratedChip} title="Customer migrated from the legacy system">
                <SyncAltIcon sx={{ fontSize: 12 }} />
                Migrated
              </span>
            )}
          </div>
          <div className={s.metaRow}>
            <span className={s.email}>{customer.email}</span>
            {renderCopyButton(customer.email, 'email', 'Copy email')}
            {countryName && (
              <span className={s.metaItem}>
                <PublicIcon sx={{ fontSize: 15 }} />
                {countryName}
              </span>
            )}
            <span className={s.metaMuted}>
              Customer since {formatShortDate(customer.createdAt)}
            </span>
          </div>
          <div className={s.metaRow}>
            <span className={s.metaMuted}>ID</span>
            <span className={s.idMono} title={customer.id}>
              {customer.id}
            </span>
            {renderCopyButton(customer.id, 'id', 'Copy customer ID')}
          </div>
          {customer.externalId && (
            <div className={s.metaRow}>
              <span className={s.metaMuted}>External ID</span>
              <span className={s.idMono} title={customer.externalId}>
                {customer.externalId}
              </span>
              {renderCopyButton(customer.externalId, 'externalId', 'Copy external ID')}
            </div>
          )}
        </div>
        {profileUrl && (
          <ExternalLinkButton
            href={profileUrl}
            label="View Customer in APP"
            icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
            ariaLabel="View customer profile (opens in new tab)"
          />
        )}
      </header>

      <nav className={s.tabs} aria-label="Customer sections">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${s.tab} ${tab === t.key ? s.tabActive : ''}`}
            onClick={() => selectTab(t.key)}
            aria-pressed={tab === t.key}>
            {t.label}
            {t.count && t.count >= 5 ? (
              <span className={s.tabCount}>{'+ ' + t.count}</span>
            ) : t.count && t.count > 0 ? (
              <span className={s.tabCount}>{t.count}</span>
            ) : null}
          </button>
        ))}
      </nav>

      <div className={s.tabContent}>
        {tab === 'overview' && <CustomerOverviewTab customer={customer} />}

        {tab === 'payments' && <CustomerPaymentsTab customerId={customer.id} />}

        {tab === 'scheduledPayments' && <CustomerScheduledPaymentsTab customer={customer} />}

        {tab === 'subscriptions' && (
          <div className={s.tableCard}>
            {customer.subscriptions.length === 0 ? (
              <div className={s.tabEmpty}>No subscriptions.</div>
            ) : (
              <table className={s.miniTable}>
                <thead>
                  <tr>
                    <th>Descriptor</th>
                    <th>Subscription</th>
                    <th>Status</th>
                    <th>Paid till</th>
                    <th>Canceled</th>
                    <th>Started</th>
                  </tr>
                </thead>
                <tbody>
                  {customer.subscriptions.map((sub) => (
                    <tr
                      key={sub.id}
                      className={s.clickableRow}
                      onClick={() => setSelectedSubscription(sub)}>
                      <td>
                        <div className={s.cellStack}>
                          <div>{sub.paymentDescriptor || '—'}</div>
                          <div className={s.subIdRow}>
                            <span className={s.idMono} title={sub.id}>
                              {sub.id}
                            </span>
                            {renderCopyButton(sub.id, `sub:${sub.id}`, 'Copy subscription ID')}
                          </div>
                        </div>
                      </td>
                      <td className={s.mono}>{getSubscriptionLabel(sub) || '—'}</td>
                      <td>
                        <SubscriptionBadge state={sub.canceledAt ? 'canceled' : 'active'} />
                      </td>
                      <td>{formatDateTimeLabel(sub.paidTill)}</td>
                      <td>
                        {sub.canceledAt ? (
                          <div className={s.cellStack}>
                            <div>{formatDateTimeLabel(sub.canceledAt)}</div>
                            {sub.canceledBy && <div className={s.sub}>by {sub.canceledBy}</div>}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      {/* Migrated subscriptions carry their original start in startedAt; createdAt is the migration moment. */}
                      <td>{formatDateTimeLabel(sub.startedAt ?? sub.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'methods' && (
          <FinancialEventCard
            title="Payment methods"
            items={buildMethodItems(customer.customerPaymentMethods ?? [])}
            emptyText="No payment methods."
          />
        )}

        {tab === 'debt' && (
          <>
            {customer.debtCollections.length > 0 && (
              <div className={s.tabToolbar}>
                <CancelDebtsButton
                  customerId={customer.id}
                  debtCollections={customer.debtCollections}
                />
              </div>
            )}
            <div className={s.tableCard}>
              {customer.debtCollections.length === 0 ? (
                <div className={s.tabEmpty}>No debt collections.</div>
              ) : (
                <table className={s.miniTable}>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Collected</th>
                      <th>Cycles</th>
                      <th>Next charge</th>
                      <th>Collected at</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.debtCollections.map((debt) => {
                      const showNextCharge =
                        ['pending', 'processing'].includes(debt.status.toLowerCase()) &&
                        debt.upcomingExecuteAt;
                      return (
                        <tr key={debt.id}>
                          <td>
                            <StatusChip
                              label={humanizeLabel(debt.status)}
                              tone={getDebtStatusTone(debt.status)}
                            />
                          </td>
                          <td className={s.amountCell}>
                            {formatDebtAmount(debt.amount, debt, currencyById)}
                          </td>
                          <td className={s.amountCell}>
                            {formatDebtAmount(debt.collectedAmount, debt, currencyById)}
                          </td>
                          <td>
                            {debt.succeededCount}/{debt.cycleCount}
                          </td>
                          <td>
                            {showNextCharge ? formatDateTimeLabel(debt.upcomingExecuteAt) : 'N/A'}
                          </td>
                          <td>{formatDateTimeLabel(debt.collectedAt)}</td>
                          <td>{formatDateTimeLabel(debt.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === 'disputes' && (
          <FinancialEventCard title="Disputes" items={disputeItems} emptyText="No disputes." />
        )}

        {tab === 'chargebacks' && (
          <FinancialEventCard
            title="Chargebacks"
            items={chargebackItems}
            emptyText="No chargeback alerts."
          />
        )}

        {tab === 'refunds' && (
          <FinancialEventCard
            title="Refunds"
            items={buildRefundItems(customer, currencyById)}
            emptyText="No refunds."
          />
        )}

        {tab === 'incidents' && <CustomerIncidentsTab incidents={customer.incidents ?? []} />}
      </div>

      <SubscriptionDetailDrawer
        subscription={selectedSubscription}
        onClose={() => setSelectedSubscription(null)}
      />
    </main>
  );
}

export default CustomerDetailPanel;
